import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Shield, Upload, X, File, AlertCircle, CheckCircle2, Clock, Lock, Eye, RefreshCw } from 'lucide-react';
import { fileRequestApi, uploadApi, embedApi } from '../services/api';

export function UploadPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';

  const [fileRequest, setFileRequest] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [embedConfig, setEmbedConfig] = useState(null);

  // Resolve embed styling: query params override API config
  const primaryColor = searchParams.get('primaryColor')
    ? `#${searchParams.get('primaryColor')}`
    : embedConfig?.primary_color || '#000000';
  const headerText = searchParams.get('headerText') || embedConfig?.header_text || '';
  const showLogo = searchParams.get('showLogo') !== 'false' && (embedConfig?.show_logo !== false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fileRequestApi.getByToken(token);
        setFileRequest(data);

        // Fetch embed config if in embed mode
        if (isEmbed) {
          try {
            const config = await embedApi.getConfig(token);
            setEmbedConfig(config);
          } catch (configErr) {
            // Non-fatal: embed will work with defaults
            console.warn('Failed to load embed config:', configErr);
          }
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Upload link not found or invalid.');
        } else if (err.response?.status === 410) {
          setError('This upload link has expired.');
        } else {
          setError('Failed to load upload request.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token, isEmbed]);

  const onDrop = useCallback((acceptedFiles) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB
    const rejectedFiles = [];

    const validFiles = acceptedFiles.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        rejectedFiles.push(file.name);
        return false;
      }
      return true;
    });

    if (rejectedFiles.length > 0) {
      setError(`The following files exceed the 5GB limit: ${rejectedFiles.join(', ')}`);
    }

    if (validFiles.length > 0) {
      const newFiles = validFiles.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        name: file.name,
        size: file.size,
        type: file.type
      }));

      setFiles(prev => {
        const combined = [...prev, ...newFiles];
        const totalSize = combined.reduce((sum, f) => sum + f.size, 0);

        if (totalSize > MAX_FILE_SIZE) {
          setError('Total upload size cannot exceed 5GB. Please remove some files.');
          return prev;
        }

        setError(null); // Clear any previous errors
        return combined;
      });
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: 5 * 1024 * 1024 * 1024 // 5GB
  });

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    // Final size check before upload
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    const MAX_SIZE = 5 * 1024 * 1024 * 1024; // 5GB

    if (totalSize > MAX_SIZE) {
      setError('Total upload size cannot exceed 5GB. Please remove some files.');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const fileObjects = files.map(f => f.file);
      const result = await uploadApi.upload(token, fileObjects, setUploadProgress);

      const successData = {
        message: 'Files uploaded successfully!',
        fileCount: result.total_files,
        files: result.files
      };
      setSuccess(successData);
      setFiles([]);

      // Notify parent window in embed mode
      if (isEmbed && window.parent !== window) {
        window.parent.postMessage({
          type: 'sft-upload-complete',
          fileCount: result.total_files,
          files: result.files,
        }, '*');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getTimeRemaining = () => {
    if (!fileRequest?.expires_at) return '';

    const now = new Date();
    const expiresAt = new Date(fileRequest.expires_at);
    const diff = expiresAt - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days !== 1 ? 's' : ''} remaining`;
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} min remaining`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
    }
  };

  // Dynamic style based on embed primary color
  const accentStyle = isEmbed && primaryColor !== '#000000'
    ? { '--embed-color': primaryColor }
    : {};

  if (loading) {
    return (
      <div className={`${isEmbed ? '' : 'min-h-screen'} bg-white flex items-center justify-center`}>
        <div className="text-center">
          <div className="spinner w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading upload request...</p>
        </div>
      </div>
    );
  }

  if (error && !fileRequest) {
    return (
      <div className={`${isEmbed ? '' : 'min-h-screen'} bg-white flex items-center justify-center`}>
        <div className="max-w-md mx-auto text-center">
          <div className="card">
            <div className="p-8">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Upload Not Available</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              {!isEmbed && (
                <Link to="/" className="btn-primary">
                  Go to Dashboard
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={`${isEmbed ? '' : 'min-h-screen'} bg-white flex items-center justify-center`} style={accentStyle}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="card">
            <div className="p-8">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Upload Successful!</h1>
              <p className="text-gray-600 mb-6">
                Your {success.fileCount} file{success.fileCount !== 1 ? 's have' : ' has'} been securely uploaded and encrypted.
              </p>

              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Files Uploaded:</h3>
                <ul className="text-left text-sm text-gray-800 space-y-1">
                  {success.files.map((file, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <File className="w-4 h-4" />
                      <span>{file.filename}</span>
                      <span className="text-gray-500">({formatFileSize(file.size)})</span>
                    </li>
                  ))}
                </ul>
              </div>

              {!isEmbed && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-6">
                  <div className="flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-primary-600" />
                    <span>Files encrypted</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-primary-600" />
                    <span>Securely stored</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4 text-primary-600" />
                    <span>Only sender can access</span>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-500 mb-4">
                The person who requested these files has been notified of your successful upload.
              </p>

              {/* Show option to upload more if link is reusable */}
              {fileRequest?.is_reusable && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-3">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">This is a reusable upload link</span>
                  </div>
                  <p className="text-sm text-blue-700 mb-4">
                    You can upload additional files using this same link until it expires.
                  </p>
                  <button
                    onClick={() => setSuccess(null)}
                    className="btn-primary inline-flex items-center space-x-2"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Upload More Files</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isEmbed ? '' : 'min-h-screen'} bg-white py-12`} style={accentStyle}>
      <div className={`${isEmbed ? 'max-w-2xl' : 'max-w-4xl'} mx-auto px-4 sm:px-6 lg:px-8`}>
        {/* Header */}
        <div className="text-center mb-8">
          {(!isEmbed || showLogo) && (
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-lg mb-4"
              style={isEmbed && primaryColor !== '#000000' ? { backgroundColor: primaryColor } : { backgroundColor: '#000000' }}
            >
              <Shield className="w-8 h-8 text-primary-500" />
            </div>
          )}
          {isEmbed && headerText ? (
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{headerText}</h1>
          ) : (
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Secure File Upload</h1>
          )}
          <p className="text-lg text-gray-600">
            Upload files for <strong>{fileRequest?.recipient_name}</strong> with enterprise-grade encryption
          </p>
        </div>

        <div className={`grid grid-cols-1 ${isEmbed ? '' : 'lg:grid-cols-3'} gap-8`}>
          {/* Upload Area */}
          <div className={`${isEmbed ? '' : 'lg:col-span-2'} space-y-6`}>
            {/* Request Details */}
            <div className="card">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Request Details</h2>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Subject:</span>
                    <p className="text-gray-900">{fileRequest?.subject}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Message:</span>
                    <p className="text-gray-900">{fileRequest?.body}</p>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="w-4 h-4 text-orange-500" />
                    <span className="text-orange-600 font-medium">
                      {getTimeRemaining()}
                    </span>
                  </div>
                  {fileRequest?.is_reusable && (
                    <div className="flex items-center space-x-2 text-sm mt-2">
                      <RefreshCw className="w-4 h-4 text-blue-500" />
                      <span className="text-blue-600 font-medium">
                        Reusable link - you can upload multiple times
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="card">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Files</h2>

                {/* Drag & Drop Area */}
                <div
                  {...getRootProps()}
                  className={`dropzone cursor-pointer ${
                    isDragActive ? 'dropzone-active' : ''
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  {isDragActive ? (
                    <p className="text-primary-600 font-medium">Drop files here...</p>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-600 mb-2">
                        <span className="font-medium">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-sm text-gray-500">Any file type, up to 100MB per file</p>
                    </div>
                  )}
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      Files to Upload ({files.length})
                    </h3>
                    <div className="space-y-2">
                      {files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <File className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            disabled={uploading}
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">Uploading files...</span>
                      <span className="text-sm text-gray-600">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                <div className="mt-6">
                  <button
                    onClick={handleUpload}
                    disabled={files.length === 0 || uploading}
                    className="btn-primary w-full flex flex-row justify-center items-center h-16"
                    style={isEmbed && primaryColor !== '#000000' ? { backgroundColor: primaryColor } : {}}
                  >
                    {uploading ? (
                      <>
                        <div className="spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3" />
                        Encrypting and Uploading...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 mr-3" />
                        Upload Files Securely
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Security Info - hidden in embed mode */}
          {!isEmbed && (
            <div className="space-y-6">
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Lock className="w-5 h-5 text-security-600" />
                    <span>Your Security</span>
                  </h3>

                  <div className="space-y-4 text-sm">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Files Encrypted</p>
                        <p className="text-gray-600">AES-256 encryption before upload</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Secure Transfer</p>
                        <p className="text-gray-600">HTTPS with certificate pinning</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Private Access</p>
                        <p className="text-gray-600">Only the requester can decrypt files</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="w-4 h-4 text-security-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">Time Limited</p>
                        <p className="text-gray-600">Upload link expires automatically</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p>
                      <strong>Large files?</strong> Files are compressed and encrypted automatically.
                    </p>
                    <p>
                      <strong>Multiple files?</strong> You can upload multiple files at once.
                    </p>
                    <p>
                      <strong>Connection issues?</strong> Your upload will resume automatically if interrupted.
                    </p>
                    <p>
                      <strong>Privacy concerns?</strong> Files are encrypted before leaving your device.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
